<template>
    <div class="card">
      <div class="card-header flex-wrap border-0 pt-6 pb-0">
        <div class="card-title">
          <div class="card-label">
            <h3 class="card-label">{{ $t('absences.title') }}</h3>
            <p class="text-muted">{{ $t('absences.list') }}</p>
          </div>
        </div>
        <div class="card-toolbar">
          <span class="me-5 badge badge-light-primary px-5 py-3 fs-5">
            {{$t('absences.available_days')}} 
            <span v-if="currentUser?.nbrHolidays || (!currentUser?.nbrHolidays && currentUser?.nbrHolidays!== 0)" class="font-weight-bolder py-1 font-size-lg text-primary ms-3">{{currentUser?.nbrHolidays || 0}}</span>
          </span>
          <ButtonDropdownFilter
            :search="false"
            :options="Object.values(absencesStatus)"
            value-field="value"
            :name-field="locale"
            v-model="query.status"
          />
          <Button type="new" btn-class="btn-sm me-2" @click="openCreateModal" btn-text="absences.demand"></Button>
        </div>
      </div>
      <div class="card-body">
        <div class="row">
          <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4"
                id="absences_datatable">
            <thead>
              <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                <th class="min-w-100px text-center">{{ $t('absences.create_date') }}</th>
                <th class="min-w-100px text-center">{{ $t('absences.start_date') }}</th>
                <th class="min-w-100px text-center">{{ $t('absences.end_date') }}</th>
                <th class="min-w-100px text-center">{{ $t('absences.type') }} </th>
                <th class="min-w-100px text-center">{{ $t('absences.number') }}</th>
                <th class="min-w-100px text-center">{{ $t('absences.status') }} </th>
                <th class="min-w-100px text-center">{{ $t('absences.actions') }}</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      <DetailModal id="detail_absence_modal" :form="absence"/>
      <CreateModal id="create_absence_modal" @load="loadData" :nbrHolidays="currentUser?.nbrHolidays"/>
      <CreateModal id="update_absence_modal" :form="formUpdateData" :edit="true" @load="loadData" :nbrHolidays="currentUser?.nbrHolidays + initialNbrDay" :dates="initialDates"/>
      <FileModal id="files_modal" :files="absence.files"/>
    </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch, defineEmits} from "vue";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import Ls from "@/services/ls";
import {BASE_URL} from "@/services/api";
import {useRouter, useRoute} from "vue-router";
import {useLocales} from "@/composables/locales";
import {useHasAbility} from "@/composables/hasAbility";
import Button from "@/components/Button";
import {Theme} from "@/composables/theming";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
import DetailModal from "./Modals/DetailsModal"
import CreateModal from "./Modals/CreateModal"
import FileModal from "./Modals/FilesAttachModal"

import {useSwal} from "@/composables/useSwal";
import { useI18n } from 'vue-i18n'

const store = useStore()
const { can } = useHasAbility()
const {showSwal} = useSwal()
const { t } = useI18n({ useScope: 'global' })

const {locale, datatableLocaleUrl} = useLocales()

const emits = defineEmits(['loadStat'])

const table = ref()
const query = ref({
  status: 'all'
})

const formUpdateData = ref({
  id: '',
  holiday_type_id: '',
  description: "",
  dates: [
    {
      start_date: "",
      end_date: "",
      nbrDay: 0
    }
  ],
  files: [],
  attach_files: [],
  detach_files: []
})

const absence = ref({})
const initialNbrDay = ref(0)
const initialDates = ref({})

const absencesStatus = computed(() => store.getters['absences/getAbsencesStatus'])
const currentUser = computed(() => store.getters['user/currentUser'])

watch(() => locale.value, async(value) => {
  if (table.value) {
    await nextTick();
    loadData();
  }
}, { deep: true })

watch(() => query.value.status, (newValue) => {
  table.value.ajax.url(`${BASE_URL}/holidays/user/datatables?status=${query.value.status}`).load()
})

function getAbsenceStatusName(status) {
  const {color, fr, en} = absencesStatus.value?.find(el => el.value === status) || ''
  return {color, fr, en}
}

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#absences_datatable").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 0, 'desc']],
    ajax: {
      url: BASE_URL + `/holidays/user/datatables?status=${query.value.status}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
    { data: "created_at" }, { data: "start_date" }, { data: "end_date" }, { data: "holiday_type_name" }, { data: "total_number_days" }, { data: "status" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [
      {
        targets: -7,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -6,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -5,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -4,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -3,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -2,
        orderable: false,
        className: "dt-center text-center" ,
        render: function (t, a, e, l) {
          let status = getAbsenceStatusName(t)
          const locale = i18n.global.locale.value
          let color = status.color
          status = status[locale]
          return '<span class="badge badge-light-' + color +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
        },
      },
      {
        width: "10%",
        targets: -1,
        "className": "dt-center text-start",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render=""
          render+='<a href="javascript:;" data-id="'+ e.id +'"  data-type="'+ e.id +'" class="btn btn-sm btn-clean btn-icon show_btn" title="'+ i18n.global.t('button.show') +'"><i  data-id="'+ e.id +'" class="fas fa-eye fs-2" style="color: '+Theme.gray +'"></i></a>'
          if(e.status == 'UNVALIDATED'){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon edit_btn" title="'+ i18n.global.t('button.edit') +'"><i  data-id="'+ e.id +'" class="fas fa-edit fs-2"  style="color: '+Theme.gray +'"></i></a>'
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon delete_btn" title="'+ i18n.global.t('button.delete') +'"><i  data-id="'+ e.id +'" class="fas fa-trash fs-2"  style="color: '+Theme.gray +'"></i></a>'
          }
          if(e.hasFile){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  data-type="'+ e.id +'" class="btn btn-sm btn-clean btn-icon files_btn" title="'+ i18n.global.t('absences.add_file') +'"><i  data-id="'+ e.id +'" class="fa fa-file fs-2" style="color: '+Theme.gray +'"></i></a>'
          }
          return render
        },
      },

    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).off("click", "#absences_datatable .show_btn").on("click", "#absences_datatable .show_btn", (e) => {
      let id = e.target.getAttribute('data-id')
      openDetailModal(id, false)
  });

  $(document).off("click", "#absences_datatable .edit_btn").on("click", "#absences_datatable .edit_btn", (e) => {
      let id = e.target.getAttribute('data-id')
      openEditModal(id)
  });

  $(document).off("click", "#absences_datatable .files_btn").on("click", "#absences_datatable .files_btn", (e) => {
      let id = e.target.getAttribute('data-id')
      openDetailModal(id, true)
  });

  $(document).off("click", "#absences_datatable .delete_btn").on("click", "#absences_datatable .delete_btn", async (e) => {
      let id = e.target.getAttribute('data-id')
      await deleteAbsence(id)
  });
}

function loadData() {
  fetchDatas()
  emits('loadStat')
}

function openDetailModal(id, getFile){
  store.dispatch('absences/showAbsence', id)
      .then((res) => {
          let data = res.data?.holiday
          if (!data) return;

          absence.value.user = data?.user?.fullname
          absence.value.type = data?.holiday_type?.name
          absence.value.start_date = data?.start_date
          absence.value.end_date = data?.end_date

          let status = getAbsenceStatusName(data?.status)
          const lang = i18n.global.locale.value

          absence.value.status = status[lang]
          absence.value.color = status['color']
          absence.value.description = data?.description
          absence.value.motif = data?.reason
          absence.value.files = []

          absence.value.validator = data.validator?.fullname
          absence.value.approved_owner = data.approved_owner?.fullname
          absence.value.rejected_owner = data.rejected_owner?.fullname

          absence.value.files = data?.files
          
          if(getFile && absence.value.files.length > 0){
            $('#files_modal').modal('toggle')
          }else{
            $('#detail_absence_modal').modal('toggle')
          }
      })
}

function openEditModal(id){
  store.dispatch('absences/showAbsence', id)
      .then((res) => {
          let data = res.data?.holiday
          if (!data) return;
          initialNbrDay.value = 0
          initialDates.value = {}

          formUpdateData.value.id = data?.id,
          formUpdateData.value.holiday_type_id = data?.holiday_type?.id
          formUpdateData.value.description = data?.description
          formUpdateData.value.dates[0].start_date = data?.start_date
          formUpdateData.value.dates[0].end_date = data?.end_date
          formUpdateData.value.dates[0].nbrDay = parseFloat(data?.total_number_days)
          initialNbrDay.value = parseFloat(data?.total_number_days)
          initialDates.value.start_date = data?.start_date
          initialDates.value.end_date = data?.end_date
          formUpdateData.value.files = []
          formUpdateData.value.attach_files = data?.files
          formUpdateData.value.detach_files = []
          $('#update_absence_modal').modal('toggle')
      })
}

function openCreateModal(){
  $('#create_absence_modal').modal('toggle')
}

async function deleteAbsence(id){
  showSwal(
      t('absences.swal.title'),
      t('absences.swal.deleting')
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('absences/deleteAbsence', id)
          .then(() => {
              loadData()
              window.toastr['success'](i18n.global.t('absences.absence_deleted_successfully'))
          })
      } catch (e) {
      }
    }
  });
}

onMounted(async() => {
  if (!absencesStatus.value.length) {
    await store.dispatch('absences/loadAbsencesStatus');
  }
  loadData()
})

</script>